/**
 * ChatHistoryItem - Individual thread preview card in the history drawer
 */

import React from 'react';
import { css } from '@emotion/react';
import { colors, spacing, fontStyles } from '../../styles';
import { ChatThread } from '../../types/chat';
import { formatRelativeTime, getThreadPreview } from '../../utils/chatHistoryUtils';

interface ChatHistoryItemProps {
  thread: ChatThread;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ thread, isActive, onSelect, onDelete }) => {
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div css={[containerStyle, isActive && activeStyle]} onClick={onSelect} role="button" tabIndex={0}>
      <div css={contentStyle}>
        <div css={titleRowStyle}>
          <span css={titleStyle}>{thread.title}</span>
          <button
            css={deleteButtonStyle}
            onClick={handleDeleteClick}
            aria-label={`Delete ${thread.title}`}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
        <p css={previewStyle}>{getThreadPreview(thread)}</p>
        <span css={timestampStyle}>{formatRelativeTime(thread.updatedAt)}</span>
      </div>
    </div>
  );
};

const containerStyle = css`
  padding: ${spacing.deci};
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  margin-bottom: ${spacing.centi};

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.08);
  }

  html[data-theme='dark'] & {
    &:hover {
      background-color: rgba(255, 255, 255, 0.04);
    }

    &:active {
      background-color: rgba(255, 255, 255, 0.08);
    }
  }
`;

const activeStyle = css`
  background-color: rgba(17, 131, 131, 0.1);

  &:hover {
    background-color: rgba(17, 131, 131, 0.15);
  }

  html[data-theme='dark'] & {
    background-color: rgba(40, 181, 189, 0.15);

    &:hover {
      background-color: rgba(40, 181, 189, 0.2);
    }
  }
`;

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.micro};
`;

const titleRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing.centi};
`;

const titleStyle = css`
  ${fontStyles.dog}
  color: ${colors.liquorice};
  margin: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  html[data-theme='dark'] & {
    color: ${colors.sugar};
  }
`;

const deleteButtonStyle = css`
  background: none;
  border: none;
  padding: ${spacing.micro};
  cursor: pointer;
  color: ${colors.silver};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
  opacity: 0.6;
  transition:
    opacity 0.15s ease,
    color 0.15s ease;

  &:hover {
    opacity: 1;
    color: ${colors.orange};
  }

  html[data-theme='dark'] & {
    color: ${colors.tungsten};

    &:hover {
      color: ${colors.orange};
    }
  }
`;

const previewStyle = css`
  ${fontStyles.mouse}
  color: ${colors.silver};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;

  html[data-theme='dark'] & {
    color: ${colors.tungsten};
  }
`;

const timestampStyle = css`
  ${fontStyles.flea}
  color: ${colors.cloud};
  margin: 0;

  html[data-theme='dark'] & {
    color: ${colors.tungsten};
  }
`;

export default ChatHistoryItem;
